import React from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Heading, Typography } from '@fiverr-private/typography';
import { I18n } from '@fiverr-private/i18n-react';
import { Gql } from '../../types';
import { MatchSeller, FreelancerPreferencesViewType } from './types';
import { LanguagePreference, LocationPreference, TimezonePreference, LevelPreference, TypePreference } from './Fields';

const T_PREFIX = 'dynamic_matching.freelancer_preferences';

const Header = ({ viewType }: { viewType: FreelancerPreferencesViewType }) => (
  <Container>
    <Heading as="h6" size="h_xs">
      <I18n k={`${T_PREFIX}.${viewType}.title`} />
    </Heading>
    {viewType === 'seller' && (
      <Typography as="p" color="grey_900" lineHeight="b_sm" size="b_sm">
        <I18n k={`${T_PREFIX}.${viewType}.description`} />
      </Typography>
    )}
  </Container>
);

const PreferencesContent = ({
  viewType,
  preferences,
  seller,
}: {
  viewType: FreelancerPreferencesViewType;
  preferences: Gql.DmBriefFreelancerPreferences;
  seller?: MatchSeller;
}) => {
  const { countries = [], regions = [], languages, timezone, freelancerType, matchIndicators } = preferences;
  const locations = { countries: countries ?? [], regions: regions ?? [] };
  const showMatchIndicator = viewType !== 'buyer';
  const isSeller = viewType === 'seller';
  const sellerName = seller?.user?.profile?.displayName || seller?.user?.fullName || seller?.user?.name;
  const { isVettedPro, agencyPreference } = freelancerType ?? {};
  const freelancerTypePreferences = {
    ...(isVettedPro ? { isVettedPro } : {}),
    ...(agencyPreference ? { agencyPreference } : {}),
  };

  const sellerMatchIndicators = matchIndicators?.find(
    (indicator) => indicator.sellerId === seller?.id || indicator.sellerId === seller?.user.id
  );

  return (
    <Stack direction="column" gap={{ default: '7', md: '4' }}>
      {languages && languages?.length > 0 && (
        <LanguagePreference
          languages={languages}
          matchIndicator={showMatchIndicator ? sellerMatchIndicators?.languages : undefined}
          sellerName={!isSeller ? sellerName : undefined}
          sellerLanguages={seller?.user.languages}
        />
      )}
      {(locations.countries.length > 0 || locations.regions.length > 0) && (
        <LocationPreference
          {...locations}
          matchIndicator={showMatchIndicator ? sellerMatchIndicators?.location : undefined}
          sellerName={sellerName}
          sellerLocation={seller?.user.address?.countryCode}
          isSeller={isSeller}
        />
      )}
      {timezone && (
        <TimezonePreference
          timezone={timezone}
          matchIndicator={showMatchIndicator ? sellerMatchIndicators?.timezone : undefined}
          sellerName={!isSeller ? sellerName : undefined}
          sellerTimezone={seller?.user.localization?.timezone}
        />
      )}
      {freelancerType?.minLevel && (
        <LevelPreference
          minLevel={freelancerType.minLevel}
          matchIndicator={showMatchIndicator ? sellerMatchIndicators?.sellerLevel : undefined}
          sellerName={sellerName}
          sellerLevel={seller?.sellerLevel}
          isSeller={isSeller}
        />
      )}
      {(freelancerTypePreferences.isVettedPro !== undefined ||
        freelancerTypePreferences.agencyPreference !== undefined) && (
        <TypePreference
          {...freelancerTypePreferences}
          matchIndicator={showMatchIndicator ? sellerMatchIndicators?.freelancerType : undefined}
          sellerName={sellerName}
          sellerIsVettedPro={seller?.isPro}
          sellerIsAgency={seller?.isSelfIdentifiedAsAgency}
          isSeller={isSeller}
        />
      )}
    </Stack>
  );
};

export const FreelancerPreferences = ({
  viewType,
  preferences,
  seller,
}: {
  viewType: FreelancerPreferencesViewType;
  preferences: Gql.DmBriefFreelancerPreferences;
  seller?: MatchSeller;
}) => (
  <Container gap="6" display="flex" direction="column">
    <Header viewType={viewType} />
    <PreferencesContent viewType={viewType} preferences={preferences} seller={seller} />
  </Container>
);
